import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="left-content line-overlay">
      <div className="register-form-container privacy">
        <h1>PRIVACY POLICY</h1>
        <p className="policy-updated">
          <strong>Last updated February 25, 2025</strong>
        </p>

        <div className="policy-callout">
          <p>
            This Privacy Notice for CLIMIFY ApS ('we', 'us', or 'our'),
            describes how and why we might access, collect, store, use, and/or
            share ('process') your personal information when you use our
            services ('Services'), including when you:
          </p>
          <ul>
            <li>
              Visit our website at{" "}
              <a href="https://climify.com" target="_blank" rel="noreferrer">
                climify.com
              </a>
              , or any website of ours that links to this Privacy Notice
            </li>
            <li>
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p>
            <strong>Questions or concerns?</strong> Reading this Privacy Notice
            will help you understand your privacy rights and choices. We are
            responsible for making decisions about how your personal information
            is processed. If you do not agree with our policies and practices,
            please do not use our Services.
          </p>
        </div>

        <div className="policy-scroll">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            SUMMARY OF KEY POINTS
          </h2>
          <p className="mb-4">
            This summary provides key points from our Privacy Notice, but you
            can find out more details about any of these topics by clicking the
            link following each key point or by using our table of contents
            below to find the section you are looking for.
          </p>
          <p className="mb-3">
            <strong>What personal information do we process?</strong> When you
            visit, use, or navigate our Services, we may process personal
            information depending on how you interact with us and the Services,
            the choices you make, and the products and features you use. Learn
            more about personal information you disclose to us.
          </p>
          <p className="mb-3">
            <strong>Do we process any sensitive personal information?</strong>{" "}
            Some of the information may be considered 'special' or 'sensitive'
            in certain jurisdictions, for example your racial or ethnic origins,
            sexual orientation, and religious beliefs. We do not process
            sensitive personal information.
          </p>
          <p className="mb-3">
            <strong>Do we collect any information from third parties?</strong>{" "}
            We do not collect any information from third parties.
          </p>
          <p className="mb-3">
            <strong>How do we process your information?</strong> We process your
            information to provide, improve, and administer our Services,
            communicate with you, for security and fraud prevention, and to
            comply with law. We may also process your information for other
            purposes with your consent. We process your information only when we
            have a valid legal reason to do so. Learn more about how we process
            your information.
          </p>
          <p className="mb-3">
            <strong>
              In what situations and with which types of parties do we share
              personal information?
            </strong>{" "}
            We may share information in specific situations and with specific
            categories of third parties. Learn more about when and with whom we
            share your personal information.
          </p>
          <p className="mb-3">
            <strong>How do we keep your information safe?</strong> We have
            adequate organisational and technical processes and procedures in
            place to protect your personal information. However, no electronic
            transmission over the internet or information storage technology can
            be guaranteed to be 100% secure, so we cannot promise or guarantee
            that hackers, cybercriminals, or other unauthorised third parties
            will not be able to defeat our security and improperly collect,
            access, steal, or modify your information. Learn more about how we
            keep your information safe.
          </p>
          <p className="mb-3">
            <strong>What are your rights?</strong> Depending on where you are
            located geographically, the applicable privacy law may mean you have
            certain rights regarding your personal information. Learn more about
            your privacy rights.
          </p>
          <p className="mb-3">
            <strong>How do you exercise your rights?</strong> The easiest way to
            exercise your rights is by visiting{" "}
            <a
              href="https://climify.com/en/contact-form"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              https://climify.com/en/contact-form
            </a>
            , or by contacting us. We will consider and act upon any request in
            accordance with applicable data protection laws.
          </p>
          <p className="mb-6">
            <strong>
              Want to learn more about what we do with any information we
              collect?
            </strong>{" "}
            Review the Privacy Notice in full.
          </p>

          <h2 className="text-2xl font-bold text-gray-800 mb-4 border-t border-gray-200 pt-6">
            TABLE OF CONTENTS
          </h2>
          <ol className="list-decimal pl-6 mb-6 space-y-1">
            <li>WHAT INFORMATION DO WE COLLECT?</li>
            <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
            <li>
              WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
              INFORMATION?
            </li>
            <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
            <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
            <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
            <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
            <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
            <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
            <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
            <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
            <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            <li>
              HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </li>
          </ol>

          <h2
            id="section1"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            1. WHAT INFORMATION DO WE COLLECT?
          </h2>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Personal information you disclose to us
          </h3>
          <p className="italic text-gray-600 mb-3">
            In Short: We collect personal information that you provide to us.
          </p>
          <p className="mb-4">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our products and Services, when you participate in activities on the
            Services, or otherwise when you contact us.
          </p>
          <p className="mb-3">
            <strong>Personal Information Provided by You.</strong> The personal
            information that we collect depends on the context of your
            interactions with us and the Services, the choices you make, and the
            products and features you use. The personal information we collect
            may include the following:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>names</li>
            <li>phone numbers</li>
            <li>email addresses</li>
            <li>mailing addresses</li>
            <li>job titles</li>
          </ul>
          <p className="mb-4">
            <strong>Sensitive Information.</strong> We do not process sensitive
            information.
          </p>
          <p className="mb-4">
            All personal information that you provide to us must be true,
            complete, and accurate, and you must notify us of any changes to
            such personal information.
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Google API
          </h3>
          <p className="mb-4">
            Our use of information received from Google APIs will adhere to
            Google API Services User Data Policy, including the Limited Use
            requirements.
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">Firebase</h3>
          <p className="mb-4">
            We use Firebase, a platform provided by Google, for various services
            including analytics, authentication, and hosting. Firebase may
            collect certain information from your device, such as IP address,
            device type, and user activity data. Firebase processes this data in
            accordance with Google's Privacy Policy. For more information on
            Firebase's data collection and processing practices, please visit{" "}
            <a
              href="https://firebase.google.com/support/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              https://firebase.google.com/support/privacy
            </a>
            .
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Microsoft Azure
          </h3>
          <p className="mb-4">
            We use Microsoft Azure cloud services to host and process certain
            data. Microsoft Azure processes and stores data in accordance with
            Microsoft's Privacy Statement and relevant data protection laws.
            Azure's global infrastructure allows us to store your data in
            specific geographic regions according to applicable legal
            requirements. For more information on Microsoft Azure's privacy
            practices, please visit{" "}
            <a
              href="https://privacy.microsoft.com/en-us/privacystatement"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              https://privacy.microsoft.com/en-us/privacystatement
            </a>
            .
          </p>

          <h2
            id="section2"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            2. HOW DO WE PROCESS YOUR INFORMATION?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We process your information to provide, improve, and
            administer our Services, communicate with you, for security and
            fraud prevention, and to comply with law. We may also process your
            information for other purposes with your consent.
          </p>
          <p className="mb-3">
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>
              To respond to user inquiries/offer support to users. We may
              process your information to respond to your inquiries and solve
              any potential issues you might have with the requested service.
            </li>
            <li>
              To send administrative information to you. We may process your
              information to send you details about our products and services,
              changes to our terms and policies, and other similar information.
            </li>
            <li>
              To fulfil and manage your orders. We may process your information
              to fulfil and manage your orders, payments, returns, and exchanges
              made through the Services.
            </li>
            <li>
              To request feedback. We may process your information when
              necessary to request feedback and to contact you about your use of
              our Services.
            </li>
            <li>
              To send you marketing and promotional communications. We may
              process the personal information you send to us for our marketing
              purposes, if this is in accordance with your marketing
              preferences. You can opt out of our marketing emails at any time.
              For more information, see 'WHAT ARE YOUR PRIVACY RIGHTS?' below.
            </li>
            <li>
              To deliver targeted advertising to you. We may process your
              information to develop and display personalised content and
              advertising tailored to your interests, location, and more.
            </li>
            <li>
              To identify usage trends. We may process information about how you
              use our Services to better understand how they are being used so
              we can improve them.
            </li>
            <li>
              To determine the effectiveness of our marketing and promotional
              campaigns. We may process your information to better understand
              how to provide marketing and promotional campaigns that are most
              relevant to you.
            </li>
            <li>
              To save or protect an individual's vital interest. We may process
              your information when necessary to save or protect an individual's
              vital interest, such as to prevent harm.
            </li>
            <li>
              To provide cloud-based services. We may process your information
              through our cloud service providers, such as Microsoft Azure, to
              deliver our Services efficiently and securely.
            </li>
            <li>
              To provide authentication services. We may process your
              information through Firebase Authentication to verify your
              identity and secure your account access.
            </li>
          </ul>

          <h2
            id="section3"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We only process your personal information when we believe
            it is necessary and we have a valid legal reason (i.e. legal basis)
            to do so under applicable law, like with your consent, to comply
            with laws, to provide you with services to enter into or fulfil our
            contractual obligations, to protect your rights, or to fulfil our
            legitimate business interests.
          </p>
          <p className="mb-4">
            The General Data Protection Regulation (GDPR) and UK GDPR require us
            to explain the valid legal bases we rely on in order to process your
            personal information. As such, we may rely on the following legal
            bases to process your personal information:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Consent.</strong> We may process your information if you
              have given us permission (i.e. consent) to use your personal
              information for a specific purpose. You can withdraw your consent
              at any time. Learn more about withdrawing your consent.
            </li>
            <li>
              <strong>Performance of a Contract.</strong> We may process your
              personal information when we believe it is necessary to fulfil our
              contractual obligations to you, including providing our Services
              or at your request prior to entering into a contract with you.
            </li>
            <li>
              <strong>Legitimate Interests.</strong> We may process your
              information when we believe it is reasonably necessary to achieve
              our legitimate business interests and those interests do not
              outweigh your interests and fundamental rights and freedoms. For
              example, we may process your personal information for some of the
              purposes described in order to:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>
                  Send users information about special offers and discounts on
                  our products and services
                </li>
                <li>
                  Develop and display personalised and relevant advertising
                  content for our users
                </li>
                <li>
                  Analyse how our Services are used so we can improve them to
                  engage and retain users
                </li>
                <li>Support our marketing activities</li>
                <li>
                  Understand how our users use our products and services so we
                  can improve user experience
                </li>
                <li>
                  Ensure the security and stability of our Services through
                  cloud infrastructure like Microsoft Azure and Firebase
                </li>
              </ul>
            </li>
            <li>
              <strong>Legal Obligations.</strong> We may process your
              information where we believe it is necessary for compliance with
              our legal obligations, such as to cooperate with a law enforcement
              body or regulatory agency, exercise or defend our legal rights, or
              disclose your information as evidence in litigation in which we
              are involved.
            </li>
            <li>
              <strong>Vital Interests.</strong> We may process your information
              where we believe it is necessary to protect your vital interests
              or the vital interests of a third party, such as situations
              involving potential threats to the safety of any person.
            </li>
          </ul>
          <p className="mb-6">
            In legal terms, we are generally the 'data controller' under
            European data protection laws of the personal information described
            in this Privacy Notice, since we determine the means and/or purposes
            of the data processing we perform. This Privacy Notice does not
            apply to the personal information we process as a 'data processor'
            on behalf of our customers. In those situations, the customer that
            we provide services to and with whom we have entered into a data
            processing agreement is the 'data controller' responsible for your
            personal information, and we merely process your information on
            their behalf in accordance with your instructions. If you want to
            know more about our customers' privacy practices, you should read
            their privacy policies and direct any questions you have to them.
          </p>

          <h2
            id="section4"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We may share information in specific situations described
            in this section and/or with the following categories of third
            parties.
          </p>
          <p className="mb-4">
            Vendors, Consultants, and Other Third-Party Service Providers. We
            may share your data with third-party vendors, service providers,
            contractors, or agents ('third parties') who perform services for us
            or on our behalf and require access to such information to do that
            work. We have contracts in place with our third parties, which are
            designed to help safeguard your personal information. This means
            that they cannot do anything with your personal information unless
            we have instructed them to do it. They will also not share your
            personal information with any organisation apart from us. They also
            commit to protect the data they hold on our behalf and to retain it
            for the period we instruct.
          </p>
          <p className="mb-3">
            The categories of third parties we may share personal information
            with are as follows:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Cloud Computing Services (including Microsoft Azure)</li>
            <li>
              Data Analytics Services (including Google Analytics and Firebase
              Analytics)
            </li>
            <li>Data Storage Service Providers</li>
            <li>Sales & Marketing Tools</li>
            <li>Social Networks</li>
            <li>Authentication Services (including Firebase Authentication)</li>
          </ul>
          <p className="mb-3">
            We also may need to share your personal information in the following
            situations:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>
              <strong>Business Transfers.</strong> We may share or transfer your
              information in connection with, or during negotiations of, any
              merger, sale of company assets, financing, or acquisition of all
              or a portion of our business to another company.
            </li>
            <li>
              <strong>Affiliates.</strong> We may share your information with
              our affiliates, in which case we will require those affiliates to
              honour this Privacy Notice. Affiliates include our parent company
              and any subsidiaries, joint venture partners, or other companies
              that we control or that are under common control with us.
            </li>
          </ul>

          <h2
            id="section5"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We may use cookies and other tracking technologies to
            collect and store your information.
          </p>
          <p className="mb-4">
            We may use cookies and similar tracking technologies (like web
            beacons and pixels) to gather information when you interact with our
            Services. Some online tracking technologies help us maintain the
            security of our Services, prevent crashes, fix bugs, save your
            preferences, and assist with basic site functions.
          </p>
          <p className="mb-4">
            We also permit third parties and service providers to use online
            tracking technologies on our Services for analytics and advertising,
            including to help manage and display advertisements, to tailor
            advertisements to your interests, or to send abandoned shopping cart
            reminders (depending on your communication preferences). The third
            parties and service providers use their technology to provide
            advertising about products and services tailored to your interests
            which may appear either on our Services or on other websites.
          </p>
          <p className="mb-3">
            Specific information about how we use such technologies and how you
            can refuse certain cookies is set out in our Cookie Notice.
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Google Analytics
          </h3>
          <p className="mb-4">
            We may share your information with Google Analytics to track and
            analyse the use of the Services. The Google Analytics Advertising
            Features that we may use include: Google Analytics Demographics and
            Interests Reporting. To opt out of being tracked by Google Analytics
            across the Services, visit{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              https://tools.google.com/dlpage/gaoptout
            </a>
            . You can opt out of Google Analytics Advertising Features through
            Ads Settings and Ad Settings for mobile apps. Other opt out means
            include{" "}
            <a
              href="http://optout.networkadvertising.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              http://optout.networkadvertising.org/
            </a>{" "}
            and{" "}
            <a
              href="http://www.networkadvertising.org/mobile-choice"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              http://www.networkadvertising.org/mobile-choice
            </a>
            . For more information on the privacy practices of Google, please
            visit the Google Privacy & Terms page.
          </p>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            Firebase Analytics
          </h3>
          <p className="mb-6">
            We use Firebase Analytics, a service provided by Google, to analyze
            how users interact with our Services. Firebase Analytics may collect
            information such as how often you use our Services, what pages you
            visit, and what features you use. This information is used to
            improve our Services and develop new features. Firebase Analytics
            uses its own cookies and technologies to collect and analyze data.
            For more information on Firebase's privacy practices, please visit{" "}
            <a
              href="https://firebase.google.com/support/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              https://firebase.google.com/support/privacy
            </a>
            .
          </p>

          <h2
            id="section6"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            6. HOW LONG DO WE KEEP YOUR INFORMATION?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We keep your information for as long as necessary to
            fulfil the purposes outlined in this Privacy Notice unless otherwise
            required by law.
          </p>
          <p className="mb-4">
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this Privacy Notice, unless a
            longer retention period is required or permitted by law (such as
            tax, accounting, or other legal requirements). No purpose in this
            notice will require us keeping your personal information for longer
            than 6 months.
          </p>
          <p className="mb-6">
            When we have no ongoing legitimate business need to process your
            personal information, we will either delete or anonymise such
            information, or, if this is not possible (for example, because your
            personal information has been stored in backup archives), then we
            will securely store your personal information and isolate it from
            any further processing until deletion is possible.
          </p>

          <h2
            id="section7"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            7. HOW DO WE KEEP YOUR INFORMATION SAFE?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We aim to protect your personal information through a
            system of organisational and technical security measures.
          </p>
          <p className="mb-3">
            We have implemented appropriate and reasonable technical and
            organisational security measures designed to protect the security of
            any personal information we process. This includes using secure
            cloud infrastructure provided by Microsoft Azure and Firebase, which
            adhere to strict security standards and compliance certifications.
          </p>
          <p className="mb-3">
            Microsoft Azure implements multiple layers of security, including
            physical security, network security, host security, application
            security, and data security. Azure complies with numerous compliance
            standards and certifications relevant to specific industries and
            regions.
          </p>
          <p className="mb-3">
            Firebase provides secure authentication methods and data storage
            options with encryption at rest and in transit for the data we
            process through their services.
          </p>
          <p className="mb-6">
            However, despite our safeguards and efforts to secure your
            information, no electronic transmission over the Internet or
            information storage technology can be guaranteed to be 100% secure,
            so we cannot promise or guarantee that hackers, cybercriminals, or
            other unauthorised third parties will not be able to defeat our
            security and improperly collect, access, steal, or modify your
            information. Although we will do our best to protect your personal
            information, transmission of personal information to and from our
            Services is at your own risk. You should only access the Services
            within a secure environment.
          </p>

          <h2
            id="section8"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            8. DO WE COLLECT INFORMATION FROM MINORS?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: We do not knowingly collect data from or market to
            children under 18 years of age.
          </p>
          <p className="mb-6">
            We do not knowingly collect, solicit data from, or market to
            children under 18 years of age, nor do we knowingly sell such
            personal information. By using the Services, you represent that you
            are at least 18 or that you are the parent or guardian of such a
            minor and consent to such minor dependent's use of the Services. If
            we learn that personal information from users less than 18 years of
            age has been collected, we will deactivate the account and take
            reasonable measures to promptly delete such data from our records.
            If you become aware of any data we may have collected from children
            under age 18, please contact us at{" "}
            <a
              href="mailto:support@climify.com"
              className="text-blue-600 hover:underline"
            >
              support@climify.com
            </a>
            .
          </p>

          <h2
            id="section9"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            9. WHAT ARE YOUR PRIVACY RIGHTS?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: In some regions, such as the European Economic Area (EEA),
            United Kingdom (UK), and Switzerland, you have rights that allow you
            greater access to and control over your personal information. You
            may review, change, or terminate your account at any time, depending
            on your country, province, or state of residence.
          </p>
          <p className="mb-3">
            In some regions (like the EEA, UK, and Switzerland), you have
            certain rights under applicable data protection laws. These may
            include the right (i) to request access and obtain a copy of your
            personal information, (ii) to request rectification or erasure;
            (iii) to restrict the processing of your personal information; (iv)
            if applicable, to data portability; and (v) not to be subject to
            automated decision-making. In certain circumstances, you may also
            have the right to object to the processing of your personal
            information. You can make such a request by contacting us by using
            the contact details provided in the section 'HOW CAN YOU CONTACT US
            ABOUT THIS NOTICE?' below.
          </p>
          <p className="mb-3">
            We will consider and act upon any request in accordance with
            applicable data protection laws.
          </p>
          <p className="mb-3">
            If you are located in the EEA or UK and you believe we are
            unlawfully processing your personal information, you also have the
            right to complain to your Member State data protection authority or
            UK data protection authority.
          </p>
          <p className="mb-3">
            If you are located in Switzerland, you may contact the Federal Data
            Protection and Information Commissioner.
          </p>
          <p className="mb-3">
            <strong>Withdrawing your consent:</strong> If we are relying on your
            consent to process your personal information, you have the right to
            withdraw your consent at any time. You can withdraw your consent at
            any time by contacting us by using the contact details provided in
            the section 'HOW CAN YOU CONTACT US ABOUT THIS NOTICE?' below.
          </p>
          <p className="mb-3">
            However, please note that this will not affect the lawfulness of the
            processing before its withdrawal nor, will it affect the processing
            of your personal information conducted in reliance on lawful
            processing grounds other than consent.
          </p>
          <p className="mb-3">
            <strong>
              Opting out of marketing and promotional communications:
            </strong>{" "}
            You can unsubscribe from our marketing and promotional
            communications at any time by clicking on the unsubscribe link in
            the emails that we send, replying 'STOP' or 'UNSUBSCRIBE' to the SMS
            messages that we send, or by contacting us using the details
            provided in the section 'HOW CAN YOU CONTACT US ABOUT THIS NOTICE?'
            below. You will then be removed from the marketing lists. However,
            we may still communicate with you — for example, to send you
            service-related messages that are necessary for the administration
            and use of your account, to respond to service requests, or for
            other non-marketing purposes.
          </p>
          <p className="mb-6">
            <strong>Cookies and similar technologies:</strong> Most Web browsers
            are set to accept cookies by default. If you prefer, you can usually
            choose to set your browser to remove cookies and to reject cookies.
            If you choose to remove cookies or reject cookies, this could affect
            certain features or services of our Services. For Firebase
            Analytics, you can opt-out through device settings or by following
            Google's opt-out instructions.
          </p>

          <h2
            id="section10"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            10. CONTROLS FOR DO-NOT-TRACK FEATURES
          </h2>
          <p className="mb-6">
            Most web browsers and some mobile operating systems and mobile
            applications include a Do-Not-Track ('DNT') feature or setting you
            can activate to signal your privacy preference not to have data
            about your online browsing activities monitored and collected. At
            this stage, no uniform technology standard for recognising and
            implementing DNT signals has been finalised. As such, we do not
            currently respond to DNT browser signals or any other mechanism that
            automatically communicates your choice not to be tracked online. If
            a standard for online tracking is adopted that we must follow in the
            future, we will inform you about that practice in a revised version
            of this Privacy Notice.
          </p>

          <h2
            id="section11"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            11. DO WE MAKE UPDATES TO THIS NOTICE?
          </h2>
          <p className="italic text-gray-600 mb-3">
            In Short: Yes, we will update this notice as necessary to stay
            compliant with relevant laws.
          </p>
          <p className="mb-6">
            We may update this Privacy Notice from time to time. The updated
            version will be indicated by an updated 'Revised' date at the top of
            this Privacy Notice. If we make material changes to this Privacy
            Notice, we may notify you either by prominently posting a notice of
            such changes or by directly sending you a notification. We encourage
            you to review this Privacy Notice frequently to be informed of how
            we are protecting your information.
          </p>

          <h2
            id="section12"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
          </h2>
          <p className="mb-3">
            If you have questions or comments about this notice, you may contact
            our support by email at{" "}
            <a
              href="mailto:support@climify.com"
              className="text-blue-600 hover:underline"
            >
              support@climify.com
            </a>
            , or contact us by post at:
          </p>
          <p className="mb-6">
            CLIMIFY ApS
            <br />
            Lyngby Hovedgade 94, st. th.
            <br />
            2800 Kongens Lyngby
            <br />
            Denmark
          </p>

          <h2
            id="section13"
            className="text-2xl font-bold text-gray-800 mb-3 border-t border-gray-200 pt-6"
          >
            13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </h2>
          <p className="mb-6">
            Based on the applicable laws of your country, you may have the right
            to request access to the personal information we collect from you,
            details about how we have processed it, correct inaccuracies, or
            delete your personal information. You may also have the right to
            withdraw your consent to our processing of your personal
            information. These rights may be limited in some circumstances by
            applicable law. To request to review, update, or delete your
            personal information, please contact us at:{" "}
            <a
              href="mailto:support@climify.com"
              className="text-blue-600 hover:underline"
            >
              support@climify.com
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
